/**
 * Hook - useWithdrawalWorkflow
 *
 * Hook pour gérer le workflow complet de retrait avec machine à états
 *
 * NOUVEAU FLUX (v2):
 * - Étape 1: Biométrie (avec statut lecteur + carte intégrés)
 *   → Initialise le lecteur, affiche le statut carte en temps réel
 *   → L'utilisateur déclenche le scan biométrique quand la carte est insérée
 *   → Après succès biométrique, les données carte sont lues automatiquement
 * - Étape 2: Vérification d'identité
 * - Étape 3: Formulaire de retrait
 * - Étape 4: Validation et distribution
 *
 * Ce hook orchestre:
 * - La machine à états WithdrawalWorkflow
 * - Les services périphériques (lecteur carte, biométrie)
 * - L'exécution du use-case ProcessWithdrawalUseCase
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useDependencies } from '../contexts/DependencyContext';
import {
  WithdrawalWorkflow,
  WithdrawalState,
  BiometricAuthResult,
  AuthenticatedClient,
} from '../../domain/aggregates/WithdrawalWorkflow';
import { Identity, IdentityDocumentType } from '../../domain/entities/Identity';
import { Money } from '../../domain/value-objects/Money';
import { TransactionResponseDTO } from '../../application/dto/TransactionResponseDTO';

export type ReaderStatus = 'disconnected' | 'checking' | 'connected';

export function useWithdrawalWorkflow() {
  const {
    accountRepository,
    cardReaderService,
    biometricService,
    withdrawalGateway,
  } = useDependencies();

  const [workflow] = useState(() => new WithdrawalWorkflow());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TransactionResponseDTO | null>(null);

  // ========== STATUT LECTEUR / CARTE ==========
  const [readerStatus, setReaderStatus] = useState<ReaderStatus>('disconnected');
  const [readerName, setReaderName] = useState<string | null>(null);
  const [cardDetected, setCardDetected] = useState(false);
  const cardPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Compteur pour forcer le re-render React après mutation du domaine
  const [, forceRender] = useState(0);

  // État actuel du workflow
  const state = workflow.state;
  const context = workflow.context;

  /**
   * Arrête le polling de présence carte
   */
  const stopCardPolling = useCallback(() => {
    if (cardPollingRef.current) {
      clearInterval(cardPollingRef.current);
      cardPollingRef.current = null;
    }
  }, []);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      stopCardPolling();
    };
  }, [stopCardPolling]);

  /**
   * Initialise le lecteur de carte et vérifie sa présence
   * Correspond au pattern de BiometricEnrollmentV2.tsx :
   *   /start → /getReaders → /selectReader → polling /cardPresent
   */
  const initializeReader = useCallback(async (): Promise<boolean> => {
    setReaderStatus('checking');
    setError(null);

    try {
      // L'adapter a une méthode initializeReader() qui fait start + getReaders + selectReader
      const cardReader = cardReaderService as any;

      if (typeof cardReader.initializeReader === 'function') {
        const result = await cardReader.initializeReader();

        if (!result.success) {
          setReaderStatus('disconnected');
          setReaderName(null);
          return false;
        }

        setReaderStatus('connected');
        setReaderName(result.reader || 'Lecteur de carte');
        return true;
      } else {
        // Fallback: vérifier si le lecteur est disponible
        const available = await cardReaderService.isAvailable();
        if (available) {
          setReaderStatus('connected');
          setReaderName('Lecteur de carte');
          return true;
        } else {
          setReaderStatus('disconnected');
          setReaderName(null);
          return false;
        }
      }
    } catch (err) {
      console.error('[Workflow] Erreur initialisation lecteur:', err);
      setReaderStatus('disconnected');
      setReaderName(null);
      return false;
    }
  }, [cardReaderService]);

  /**
   * Démarre le polling LÉGER de détection de carte.
   * Utilise /cardPresent (retourne 0/1/2) au lieu du lourd authenticateCard.
   * Cela permet de vérifier la présence sans relancer l'auth mutuelle à chaque fois.
   */
  const startCardPolling = useCallback(() => {
    stopCardPolling();

    console.log('[Workflow] Démarrage polling léger détection carte...');

    cardPollingRef.current = setInterval(async () => {
      try {
        const cardReader = cardReaderService as any;

        // Utiliser la méthode légère (juste /cardPresent)
        if (typeof cardReader.checkCardPresenceLight === 'function') {
          const status = await cardReader.checkCardPresenceLight();
          setCardDetected(status.present);
        } else {
          // Fallback: utiliser isCardPresent synchrone
          setCardDetected(cardReaderService.isCardPresent());
        }
      } catch (err) {
        console.debug('[Workflow] Polling carte en cours...');
      }
    }, 2000);
  }, [cardReaderService, stopCardPolling]);

  /**
   * Rafraîchir le statut du lecteur (bouton refresh)
   */
  const refreshReaderStatus = useCallback(async () => {
    const success = await initializeReader();
    if (success) {
      startCardPolling();
    }
  }, [initializeReader, startCardPolling]);

  /**
   * NOUVEAU: Étape biométrique combinée
   * 1. STOPPE le polling carte (évite requêtes concurrentes sur le lecteur)
   * 2. Lance le scan biométrique (forceRestart + verification)
   * 3. Après succès, utilise les données DÉJÀ lues par l'adapter bio
   *    (authResult.clientId = numéro de compte, authResult.clientName = nom)
   *    → NE PAS rappeler checkCardStatus car le service est arrêté après bio
   */
  const startBiometricAuth = useCallback(async (): Promise<BiometricAuthResult | null> => {
    setLoading(true);
    setError(null);

    try {
      // ══════ CORRECTION 1: Stopper le polling AVANT le scan ══════
      // Le lecteur carte est une ressource unique — les requêtes /cardPresent
      // du polling entrent en conflit avec les commandes APDU du scan biométrique
      stopCardPolling();
      console.log('[Workflow] Polling carte stoppé avant scan biométrique');

      // Transition directe IDLE → BIOMETRIC_PENDING
      workflow.startBiometricAuth();

      // Lancer le scan biométrique (forceRestart + verification)
      await biometricService.startFingerprintScan();
      const authResult = await biometricService.waitForScan();

      if (authResult.success) {
        // La vérification biométrique a réussi → la carte est forcément présente
        setCardDetected(true);

        // ══════ CORRECTION 2: Utiliser authResult directement ══════
        // L'adapter biométrique (RealBiometricFingerprintAdapter.waitForScan)
        // lit DÉJÀ les données carte (getCardData + getCardNum) après la
        // vérification, puis appelle stopService(). Il ne faut PAS rappeler
        // checkCardStatus() car cela relancerait authenticateCard sur un
        // service arrêté → erreur 6A86.
        //
        // authResult.clientId contient le numéro de compte (13+ chiffres)
        // authResult.clientName contient le nom complet du client
        const accountNumber = authResult.clientId;

        if (accountNumber && accountNumber !== 'CARD-USER') {
          try {
            const account = await accountRepository.findByNumberString(accountNumber);
            if (account) {
              workflow.setAccount(account);
              console.log('[Workflow] Compte associé après biométrie:', accountNumber);
            } else {
              console.warn('[Workflow] Compte non trouvé en base:', accountNumber);
            }
          } catch (dbErr) {
            console.warn('[Workflow] Erreur recherche compte:', dbErr);
          }
        } else {
          console.warn('[Workflow] Pas de numéro de compte dans authResult');
        }
      }

      // Compléter la transition biométrique
      workflow.completeBiometricAuth(authResult);

      return authResult;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'authentification');
      return null;
    } finally {
      setLoading(false);
    }
  }, [workflow, biometricService, accountRepository, stopCardPolling]);

  /**
   * Étape 3: Vérifier l'identité
   */
  const verifyIdentity = useCallback(
    async (
      clientId: string,
      clientName: string,
      biometricAuthId: string,
      identityData: {
        type: IdentityDocumentType;
        number: string;
        issueDate: Date;
        expiryDate: Date;
        issueCountry: string;
        issuePlace: string;
        holderName: string;
      }
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        // Si on n'est pas encore en IDENTITY_VERIFICATION, y aller
        if (workflow.state === 'BIOMETRIC_SUCCESS') {
          workflow.startIdentityVerification();
        }

        // Créer l'objet Identity avec validation
        const identity = Identity.create(identityData);

        const authenticatedClient: AuthenticatedClient = {
          clientId,
          clientName,
          biometricAuthId,
          identity,
          authenticatedAt: new Date(),
        };

        workflow.completeIdentityVerification(authenticatedClient);
        // Forcer le re-render pour que WithdrawalPage lise le nouvel état IDENTITY_VERIFIED
        forceRender(n => n + 1);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors de la vérification d\'identité');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [workflow, forceRender]
  );

  /**
   * Passer à la vérification d'identité (après biométrie réussie)
   */
  const proceedToIdentityVerification = useCallback(() => {
    try {
      workflow.startIdentityVerification();
      // Forcer un re-render pour que les composants lisent le nouvel état
      forceRender(n => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [workflow]);

  /**
   * Passer au formulaire de retrait
   */
  const proceedToForm = useCallback(() => {
    try {
      workflow.proceedToWithdrawalForm();
      forceRender(n => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [workflow, forceRender]);

  /**
   * Définir les détails du retrait
   */
  const setWithdrawalDetails = useCallback(
    (amount: number, denomination: Map<number, number>, destination: string, notes?: string) => {
      try {
        const money = Money.create(amount, 'XOF');
        workflow.setWithdrawalDetails(money, denomination, destination, notes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur');
      }
    },
    [workflow]
  );

  /**
   * Soumettre et exécuter le retrait via le backend CardLife
   *
   * Flux multi-signatures :
   * 1. POST /operation-retraits/complet → Initialise le retrait (EN_ATTENTE)
   * 2. POST /{id}/signer → Ajoute la signature de la carte authentifiée
   *    → Si c'est la dernière signature requise, le backend appelle CBS /v3/retrait
   *    → Retourne le PDF du bordereau
   */
  const submitWithdrawal = useCallback(
    async (cashierId: string, _cashierName: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        workflow.submitForValidation();

        const currentContext = workflow.context;
        // Utiliser le numéro de carte du résultat biométrique (account peut être undefined)
        const accountNumber = currentContext.account?.accountNumber?.value
          ?? currentContext.lastBiometricResult?.clientId
          ?? currentContext.authenticatedClients[0]?.clientId
          ?? '';
        if (!accountNumber) {
          throw new Error('Numéro de compte/carte non disponible');
        }
        const denomination = Object.fromEntries(
          currentContext.requestedDenomination!.entries()
        ) as Record<number, number>;

        // Nombre de signataires = nombre de clients authentifiés (min 1)
        const nbSignatures = Math.max(1, currentContext.authenticatedClients.length);

        if (!withdrawalGateway) {
          throw new Error('Le gateway de retrait n\'est pas configuré (VITE_USE_REMOTE_WITHDRAWAL=false)');
        }

        // ═══════ ÉTAPE 1 : Initialiser le retrait (Opération + Billetage + Retrait) ═══════
        console.log('[Withdrawal] Étape 1: POST /operation-retraits/complet');
        const initResponse = await withdrawalGateway.initializeWithdrawal({
          accountNumber,
          amount: currentContext.requestedAmount!.value,
          denomination,
          nbSignatures,
          cashierId,
          notes: currentContext.notes || 'Retrait espèces',
          motifRetrait: currentContext.destination || 'Retrait espèces',
        });

        const retraitId = initResponse.retrait.id;
        console.log('[Withdrawal] Retrait initialisé, ID:', retraitId, 'Statut:', initResponse.retrait.statut);

        // ═══════ ÉTAPE 2 : Ajouter les signatures des cartes authentifiées ═══════
        let finalResponse = initResponse;

        for (let i = 0; i < currentContext.authenticatedClients.length; i++) {
          const client = currentContext.authenticatedClients[i];
          console.log(`[Withdrawal] Étape 2.${i + 1}: POST /${retraitId}/signer (carte: ${client.clientId})`);

          finalResponse = await withdrawalGateway.addSignature(retraitId, {
            cardNumber: client.clientId, // Le numéro de carte vient de l'auth biométrique
            ordre: i + 1,
            pinVerified: true,
            fingerprintVerified: true,
          });

          console.log(`[Withdrawal] Signature ${i + 1}/${nbSignatures} ajoutée. Statut:`, finalResponse.retrait.statut);
        }

        // ═══════ ÉTAPE 3 : Vérifier le résultat CBS ═══════
        if (finalResponse.retrait.statut === 'EXECUTEE') {
          console.log('[Withdrawal] Retrait exécuté avec succès au CBS!');
          console.log('[Withdrawal] Référence opération:', finalResponse.operationReference ?? '(non fournie par le backend)');
          console.log('[Withdrawal] Référence externe:', finalResponse.externalReference ?? '(non fournie)');

          // Construire la réponse pour le composant TransactionReceipt.
          // Le reçu du backend fait foi : compte réellement débité, titulaires, agence.
          const recu = finalResponse.recu;
          const transactionResponse: TransactionResponseDTO = {
            id: String(finalResponse.retrait.operationId || retraitId),
            type: 'WITHDRAWAL',
            amount: recu?.montant ?? currentContext.requestedAmount!.value,
            currency: recu?.devise ?? 'XOF',
            accountNumber: recu?.numcpt ?? accountNumber,
            clientId: currentContext.authenticatedClients.map(c => c.clientId).join(', '),
            clientName: recu?.intituleCompte
              || currentContext.authenticatedClients.map(c => c.clientName).join(' / '),
            agence: recu?.agence ?? undefined,
            signataires: recu?.signataires,
            cashierId,
            cashierName: _cashierName,
            createdAt: recu?.dateOperation ?? new Date().toISOString(),
            reference: recu?.reference
              ?? finalResponse.operationReference
              ?? finalResponse.externalReference
              ?? `RET-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${retraitId}`,
            biotransacRef: finalResponse.operationReference
              ?? `BT-${retraitId}`,
            status: 'COMPLETED',
            notes: currentContext.notes,
            denomination,
            destination: currentContext.destination,
            receiptPdfBase64: finalResponse.pdfBase64,
            externalReference: finalResponse.externalReference,
          };

          workflow.complete(transactionResponse.id);
          setResult(transactionResponse);
          return true;
        } else {
          // Le retrait n'est pas encore exécuté (encore en attente de signatures)
          const msg = `Retrait en attente: ${finalResponse.retrait.nbrsignaturesEffectives}/${finalResponse.retrait.nbrsignataire} signatures`;
          console.warn('[Withdrawal]', msg);
          workflow.fail(msg);
          setError(msg);
          return false;
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erreur inconnue';
        console.error('[Withdrawal] Erreur:', errorMsg);
        workflow.fail(errorMsg);
        setError(errorMsg);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      workflow,
      withdrawalGateway,
    ]
  );

  /**
   * Ajouter un autre signataire (compte joint)
   * Renvoie le workflow à l'état BIOMETRIC_PENDING pour le prochain co-titulaire
   */
  const addAnotherClient = useCallback(() => {
    try {
      workflow.addAnotherClient();
      console.log('[Workflow] Retour à BIOMETRIC_PENDING pour le prochain co-titulaire');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [workflow]);

  /**
   * Annuler le workflow
   */
  const cancel = useCallback(() => {
    try {
      stopCardPolling();
      // Déjà terminée (annulée, réussie ou échouée) : rien à annuler
      if (
        workflow.state === WithdrawalState.CANCELLED ||
        workflow.state === WithdrawalState.COMPLETED ||
        workflow.state === WithdrawalState.FAILED
      ) {
        return;
      }
      workflow.cancel();
      cardReaderService.ejectCard();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [workflow, cardReaderService, stopCardPolling]);

  /**
   * Réinitialiser le workflow
   */
  const reset = useCallback(() => {
    stopCardPolling();
    workflow.reset();
    setError(null);
    setResult(null);
    setReaderStatus('disconnected');
    setReaderName(null);
    setCardDetected(false);
  }, [workflow, stopCardPolling]);

  return {
    // État
    state,
    context,
    loading,
    error,
    result,

    // Statut lecteur / carte
    readerStatus,
    readerName,
    cardDetected,

    // Actions lecteur
    initializeReader,
    startCardPolling,
    stopCardPolling,
    refreshReaderStatus,

    // Actions workflow
    startBiometricAuth,
    verifyIdentity,
    proceedToIdentityVerification,
    proceedToForm,
    addAnotherClient,
    setWithdrawalDetails,
    submitWithdrawal,
    cancel,
    reset,

    // Helpers
    canCancel: workflow.canCancel(),
    canRetry: workflow.canRetry(),
    isCompleted: workflow.isCompleted(),
    isFailed: workflow.isFailed(),
  };
}
